// Components==============
import indexCategories from 'actions/category/index';
import indexSessions from 'actions/training/session/index';
import indexTraining from 'actions/training/training/index';
import Layout from 'components/Layout/Layout';
import Container from 'components/UI/Container/Container';
import TrainingLayout from 'components/UI/TrainingLayout/TrainingLayout';
import { AnimatePresence } from 'framer-motion';
import { useFormValues } from 'hooks/useFormValues';
import { useMediaQ } from 'hooks/useMediaQ';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import React, { createContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { EvidenceState } from 'typings';
import Menu from './components/Menu/Menu';
import SessionEditor from './components/SessionEditor/SessionEditor';
import TrainingEditor from './components/TrainingEditor/TrainingEditor';
import './TrainingBuilder.module.scss';
import {
  TrainingBuilderFunctions,
  useTrainingBuilderFunctions,
} from './useTrainingBuilderFunctions';
// =========================

type FormValues = {
  id: string | undefined;
  onlyTraining: boolean;
  title: string;
  categories: number[];
  skills: number[];
  description: string;
  youtube: string;
};

export type AttachmentType = {
  id: number;
  data?: { title: string; text: string };
  order_index: number;
  attachment_type: string;
  file?: File;
  changed: boolean;
};

type EditorView = 'training' | 'session';

export type LessonContentType = {
  id: number;
  order_index: number;
  block_type: string;
  block_data: string | { file?: File };
  block_local_data?: string;
  changed: boolean;
};

export type LessonType = {
  id: number;
  title: string;
  description: string;
  order_index: number;
  is_published?: 0 | 1;
  evidence_required: 0 | 1;
  contents?: LessonContentType[];
  changed: boolean;
};

export type SessionType = {
  id: number;
  title: string;
  image?: string;
  is_published?: 0 | 1;
  imageFile?: File;
  description: string;
  lessons?: LessonType[];
  order_index: number;
  changed: boolean;
};

export type TrainingBuilderContextType = {
  training?: TrainingEntity;
  trainingId?: string;
  formValues: FormValues;
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  errors: FormValues;
  setErrors: React.Dispatch<any>;
  setFormValues: React.Dispatch<React.SetStateAction<FormValues>>;
  images: EvidenceState[];
  setImages: React.Dispatch<React.SetStateAction<EvidenceState[]>>;
  attachments: AttachmentType[];
  setAttachments: React.Dispatch<React.SetStateAction<AttachmentType[]>>;
  editorView: EditorView;
  setEditorView: React.Dispatch<React.SetStateAction<EditorView>>;
  currentSession?: number;
  setCurrentSession: React.Dispatch<React.SetStateAction<number | undefined>>;
  sessions: SessionType[];
  setSessions: React.Dispatch<React.SetStateAction<SessionType[]>>;
  trainingSessions?: (TrainingSessionEntity | undefined)[];
  saveCounter: number;
  setSaveCounter: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
};

export const TrainingBuilderContext = createContext(
  {} as TrainingBuilderContextType & TrainingBuilderFunctions
);

export default function TrainingBuilder() {
  const dispatch = useReduxAction();
  const history = useHistory();

  const [editorView, setEditorView] = useState<EditorView>('training');

  const { trainingId } = useParams() as {
    trainingId?: string;
  };

  const [saveCounter, setSaveCounter] = useState(0);

  useEffect(() => {
    dispatch(indexCategories());

    if (trainingId) {
      dispatch(indexTraining());
      dispatch(indexSessions({ trainingId }));
    }
  }, []);

  const training = useRedux((state) =>
    trainingId ? state.training?.entities.byId[trainingId] : undefined
  );

  const trainingSessions = useRedux((state) =>
    state.trainingSession?.entities.allIds
      .map((id) => state.trainingSession?.entities.byId[id])
      .filter((session) => `${session?.training_id}` === trainingId)
  );

  const sessionEntity = trainingSessions?.length
    ? (trainingSessions
        ?.sort((a, b) => (a?.order_index || 0) - (b?.order_index || 0))
        .map((s) => ({
          id: s?.id,
          title: s?.title,
          is_published: s?.is_published,
          image: s?.image,
          description: s?.description || '',
          lessons: s?.lessons?.map(
            (l) =>
              ({
                id: l.id,
                title: l.title || '',
                description: l.description || '',
                is_published: l?.is_published,
                order_index: l.order_index || 0,
                evidence_required: l.evidence_required,
                contents: l.contents.map(
                  (c) =>
                    ({
                      id: c.id,
                      order_index: c.order_index,
                      block_type: c.block_type,
                      block_data: c.block_data,
                      changed: false,
                    } as LessonContentType)
                ),
                changed: false,
              } as LessonType)
          ),
          order_index: s?.order_index || 0,
          changed: false,
        })) as SessionType[])
    : [];

  const [sessions, setSessions] = useState<SessionType[]>(sessionEntity);

  useEffect(() => {
    if (sessions.length === 0) setSessions(sessionEntity);
  }, [trainingSessions?.length]);

  const [currentSession, setCurrentSession] = useState<number | undefined>(
    sessions?.[0]?.id || undefined
  );

  useEffect(() => {
    if (editorView === 'training') setCurrentSession(undefined);
  }, [editorView]);

  const {
    formValues,
    handleChange,
    errors,
    setErrors,
    setFormValues,
  } = useFormValues({
    id: trainingId,
    onlyTraining: false,
    title: '',
    categories: [] as number[],
    skills: [] as number[],
    description: '',
    youtube: '',
  });

  const [images, setImages] = useState(
    training?.attachments
      ?.filter((a) => a.attachment_type === 'image')
      .map((a) => {
        const data = JSON.parse(a?.attachment_data) as {
          file_name: string;
          file_path: string;
        };

        return { name: data.file_name, path: data.file_path, evidenceId: a.id };
      }) || []
  );

  const attachmentEntity =
    training?.attachments?.map((a) => ({
      id: a.id,
      data: JSON.parse(a.attachment_data) || undefined,
      order_index: a.order_index || 0,
      attachment_type: a.attachment_type,
      file: undefined,
      changed: false,
    })) || [];

  const [attachments, setAttachments] = useState<AttachmentType[]>(
    attachmentEntity
  );

  useEffect(() => {
    // Reset the changed states to false
    if (saveCounter > 0) {
      setSessions(sessionEntity);
      setAttachments(attachmentEntity);
    }
  }, [saveCounter]);

  const query = useMediaQ('min', 1024);

  useEffect(() => {
    if (!query) history.push('/werkplaats/mijn-trainingen');
  }, [query]);

  const loading = !!useRedux(
    (state) =>
      state.training?.loading ||
      state.category?.loading ||
      state.trainingSession?.loading ||
      state.skill?.loading
  );

  const contextValues = {
    training,
    trainingId,
    formValues,
    handleChange,
    errors,
    setErrors,
    setFormValues,
    images,
    setImages,
    attachments,
    setAttachments,
    editorView,
    setEditorView,
    currentSession,
    setCurrentSession,
    sessions,
    setSessions,
    trainingSessions,
    saveCounter,
    setSaveCounter,
    loading,
  };

  const functions = useTrainingBuilderFunctions(contextValues);

  return (
    <Layout grey>
      <TrainingBuilderContext.Provider
        value={{ ...functions, ...contextValues }}
      >
        <Container>
          <AnimatePresence>
            <TrainingLayout
              content={
                <AnimatePresence>
                  <TrainingEditor key={1} />
                  <SessionEditor key={2} />
                </AnimatePresence>
              }
              menu={<Menu />}
            />
          </AnimatePresence>
        </Container>
      </TrainingBuilderContext.Provider>
    </Layout>
  );
}
